<template>
  <div ref="root" class="city-picker">
    <input
      :id="inputId"
      v-model="query"
      type="text"
      :placeholder="placeholder"
      autocomplete="off"
      @focus="onFocus"
      @input="onInput"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape="closeSuggestions"
    />
    <button
      v-if="hasSelection"
      type="button"
      class="btn btn-secondary city-picker-clear"
      @click="clear"
    >
      Очистить
    </button>
    <ul v-if="showSuggestions" class="city-picker-suggestions">
      <li
        v-for="(suggestion, index) in suggestions"
        :key="`${suggestion.lat}-${suggestion.lng}-${index}`"
        :class="{ highlighted: index === highlightedIndex }"
        @mousedown.prevent="selectSuggestion(suggestion)"
      >
        <template v-if="mode === 'place'">
          {{ suggestion.hint || suggestion.city }}
        </template>
        <template v-else-if="suggestion.hint && suggestion.hint !== suggestion.city">
          <span class="suggestion-primary">{{ suggestion.city }}</span>
          <span class="suggestion-secondary">{{ suggestion.hint }}</span>
        </template>
        <template v-else>
          {{ suggestion.city }}
        </template>
      </li>
    </ul>
    <p v-if="searchError" class="error-message">{{ searchError }}</p>
    <p v-else-if="searching" class="field-hint">Поиск...</p>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { searchCities } from '../utils/geocoding'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  inputId: {
    type: String,
    default: 'profile-city',
  },
  placeholder: {
    type: String,
    default: 'Начните вводить название города',
  },
  mode: {
    type: String,
    default: 'city',
    validator: (value) => ['city', 'place'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue'])

const root = ref(null)
const query = ref('')
const selected = ref(null)
const suggestions = ref([])
const highlightedIndex = ref(-1)
const searching = ref(false)
const searchError = ref('')
const showSuggestions = ref(false)
let debounceTimer = null
let requestId = 0

const hasSelection = computed(() => Boolean(selected.value?.city))

function closeSuggestions() {
  showSuggestions.value = false
  highlightedIndex.value = -1
}

function onFocus() {
  if (suggestions.value.length) {
    showSuggestions.value = true
  }
}

function onInput() {
  selected.value = null
  scheduleSearch()
}

function scheduleSearch() {
  clearTimeout(debounceTimer)
  searchError.value = ''

  const trimmed = query.value.trim()
  if (trimmed.length < 2) {
    suggestions.value = []
    closeSuggestions()
    return
  }

  debounceTimer = setTimeout(() => {
    void runSearch(trimmed)
  }, 400)
}

async function runSearch(trimmed) {
  const currentRequestId = ++requestId
  searching.value = true

  try {
    const results = await searchCities(trimmed, { mode: props.mode })
    if (currentRequestId !== requestId) return

    suggestions.value = results
    highlightedIndex.value = results.length ? 0 : -1
    showSuggestions.value = results.length > 0
  } catch {
    if (currentRequestId !== requestId) return
    searchError.value = 'Не удалось найти город'
    suggestions.value = []
    closeSuggestions()
  } finally {
    if (currentRequestId === requestId) {
      searching.value = false
    }
  }
}

function selectSuggestion(suggestion) {
  const next = { city: suggestion.city, lat: suggestion.lat, lng: suggestion.lng }
  selected.value = next
  query.value = suggestion.city
  emit('update:modelValue', next)
  closeSuggestions()
}

function moveHighlight(delta) {
  if (!suggestions.value.length) return

  const next = highlightedIndex.value + delta
  if (next < 0) {
    highlightedIndex.value = suggestions.value.length - 1
    return
  }
  if (next >= suggestions.value.length) {
    highlightedIndex.value = 0
    return
  }
  highlightedIndex.value = next
}

function selectHighlighted() {
  if (highlightedIndex.value < 0) return
  selectSuggestion(suggestions.value[highlightedIndex.value])
}

function clear() {
  query.value = ''
  selected.value = null
  suggestions.value = []
  searchError.value = ''
  emit('update:modelValue', null)
  closeSuggestions()
}

function getSelection() {
  return selected.value ? { ...selected.value } : null
}

function hasUnconfirmedInput() {
  const queryText = query.value.trim()
  if (!queryText) return false

  return queryText !== (selected.value?.city?.trim() ?? '')
}

defineExpose({ hasUnconfirmedInput, getSelection })

function onDocumentClick(event) {
  if (!root.value?.contains(event.target)) {
    closeSuggestions()
  }
}

watch(
  () => props.modelValue,
  (value) => {
    const queryText = query.value.trim()
    const incomingCity = value?.city?.trim() ?? ''
    if (queryText && queryText !== incomingCity) return

    selected.value = value ? { ...value } : null
    query.value = value?.city ?? ''
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  clearTimeout(debounceTimer)
  document.removeEventListener('click', onDocumentClick)
})
</script>

<style scoped>
.city-picker {
  position: relative;
}

.city-picker-clear {
  margin-top: 0.5rem;
}

.city-picker-suggestions {
  position: absolute;
  z-index: 20;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  max-height: 14rem;
  overflow-y: auto;
}

.city-picker-suggestions li {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}

.suggestion-primary {
  display: block;
}

.suggestion-secondary {
  display: block;
  margin-top: 0.125rem;
  color: #64748b;
  font-size: 0.875rem;
}

.city-picker-suggestions li:hover,
.city-picker-suggestions li.highlighted {
  background: #f1f5f9;
}

.field-hint {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 0.875rem;
}
</style>
